import * as i0 from '@angular/core';
import { input, output, model, signal, computed, Component, ChangeDetectionStrategy } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { NgxIcon } from '@digipay/ngx-icon';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';
const _c0 = [[["", "leadingComponent", ""]], [["", "trailingComponent", ""]]];
const _c1 = ["[leadingComponent]", "[trailingComponent]"];
function NgxChipComponent_Conditional_0_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "ngx-icon", 2);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("name", ctx_r1.leadingIcon())("type", "linear")("iconSize", "16px")("classes", ctx_r1.generatedTextClasses());
  }
}
function NgxChipComponent_Conditional_0_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵlistener("click", function NgxChipComponent_Conditional_0_Conditional_6_Template_div_click_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.handleTrailingChipClick($event));
    });
    i0.ɵɵelement(1, "ngx-icon", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("@.disabled", ctx_r1.disableAnimations())("@appear", undefined);
    i0.ɵɵadvance();
    i0.ɵɵproperty("name", ctx_r1.trailingIcon())("type", "linear")("iconSize", "16px")("classes", ctx_r1.generatedTextClasses());
  }
}
function NgxChipComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵlistener("mouseenter", function NgxChipComponent_Conditional_0_Template_div_mouseenter_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.handleWrapperHover(true));
    })("mouseleave", function NgxChipComponent_Conditional_0_Template_div_mouseleave_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.handleWrapperHover(false));
    })("click", function NgxChipComponent_Conditional_0_Template_div_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.handlePress());
    });
    i0.ɵɵprojection(1);
    i0.ɵɵtemplate(2, NgxChipComponent_Conditional_0_Conditional_2_Template, 1, 4, "ngx-icon", 2);
    i0.ɵɵelementStart(3, "div");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵprojection(5, 1);
    i0.ɵɵtemplate(6, NgxChipComponent_Conditional_0_Conditional_6_Template, 2, 6, "div", 3);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassMap("chip d-flex flex-row radius-giant align-items-center border-color-light surface-elevated  " + ctx_r1.generatedWrapperClasses() + " " + ctx_r1.classes());
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(2, ctx_r1.leadingIcon() && ctx_r1.leadingIcon() !== "" ? 2 : -1);
    i0.ɵɵadvance();
    i0.ɵɵclassMap("chip-label px-tiny " + ctx_r1.sizeToTextTypographyMap[ctx_r1.size()] + " " + ctx_r1.generatedTextClasses());
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r1.label(), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(6, ctx_r1.trailingIcon() && ctx_r1.trailingIcon() !== "" && ctx_r1.pressed() ? 6 : -1);
  }
}
function NgxChipComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "lib-ngx-skeleton-loading", 5);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("classes", "radius-giant")("height", ctx_r1.size() === "MEDIUM" ? "28px" : ctx_r1.size() === "SMALL" ? "24px" : "40px")("width", ctx_r1.size() === "MEDIUM" ? "111px" : ctx_r1.size() === "SMALL" ? "98px" : "125px");
  }
}
let NgxChipComponent = /*#__PURE__*/(() => {
  class NgxChipComponent {
    constructor() {
      // Inputs
      this.id = input.required();
      this.size = input('MEDIUM');
      this.disabled = input(false);
      this.disableAnimations = input(false);
      this.isLoading = input(false);
      this.leadingIcon = input('');
      this.disableClick = input(false);
      this.trailingIcon = input('');
      this.label = input('');
      this.stopPropagationOnTrailingIconClick = input(true);
      this.classes = input('');
      // Outputs
      this.trailingIconClicked = output();
      // Models
      this.pressed = model(false);
      // Variables
      this.hovered = signal(false);
      this.generatedTextClasses = computed(() => this.disabled() ? 'text-onback-disabled' : this.pressed() ? 'text-onback-brand' : '');
      this.generatedWrapperClasses = computed(() => this.generateWrapperClasses());
      this.sizeToTextTypographyMap = {
        SMALL: 'l-2',
        MEDIUM: 'c-2',
        LARGE: 'bt-2'
      };
    }
    handleTrailingChipClick(event) {
      if (!this.disabled()) {
        if (this.stopPropagationOnTrailingIconClick()) {
          event.stopPropagation();
          event.preventDefault();
        }
        this.trailingIconClicked.emit(this.id());
      }
    }
    handleWrapperHover(isHovered) {
      this.hovered.set(isHovered);
    }
    handlePress() {
      if (this.disableClick()) {
        return;
      }
      if (!this.disabled()) {
        this.pressed.set(!this.pressed());
      }
    }
    generateWrapperClasses() {
      const size = this.size();
      const classes = [];
      // Size-based classes
      if (size === 'LARGE') {
        classes.push('p-small', 'h-40');
      } else if (size === 'MEDIUM') {
        classes.push('px-small', 'py-tiny', 'h-28');
      } else if (size === 'SMALL') {
        classes.push('px-low', 'py-tiny', 'h-24');
      }
      // Hover-based classes
      classes.push(this.hovered() ? 'border-500' : 'border-300');
      // Pressed and disabled-based classes
      if (this.pressed()) {
        classes.push(this.disabled() ? 'surface-back' : 'border-color-brand surface-brand-tint');
      } else if (this.disabled()) {
        classes.push('border-color-disabled');
      }
      return classes.join(' ');
    }
    static {
      this.ɵfac = function NgxChipComponent_Factory(t) {
        return new (t || NgxChipComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgxChipComponent,
        selectors: [["ngx-chip"]],
        inputs: {
          id: [i0.ɵɵInputFlags.SignalBased, "id"],
          size: [i0.ɵɵInputFlags.SignalBased, "size"],
          disabled: [i0.ɵɵInputFlags.SignalBased, "disabled"],
          disableAnimations: [i0.ɵɵInputFlags.SignalBased, "disableAnimations"],
          isLoading: [i0.ɵɵInputFlags.SignalBased, "isLoading"],
          leadingIcon: [i0.ɵɵInputFlags.SignalBased, "leadingIcon"],
          disableClick: [i0.ɵɵInputFlags.SignalBased, "disableClick"],
          trailingIcon: [i0.ɵɵInputFlags.SignalBased, "trailingIcon"],
          label: [i0.ɵɵInputFlags.SignalBased, "label"],
          stopPropagationOnTrailingIconClick: [i0.ɵɵInputFlags.SignalBased, "stopPropagationOnTrailingIconClick"],
          classes: [i0.ɵɵInputFlags.SignalBased, "classes"],
          pressed: [i0.ɵɵInputFlags.SignalBased, "pressed"]
        },
        outputs: {
          trailingIconClicked: "trailingIconClicked",
          pressed: "pressedChange"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c1,
        decls: 2,
        vars: 1,
        consts: [[3, "class"], [3, "mouseenter", "mouseleave", "click"], [3, "name", "type", "iconSize", "classes"], [1, "trailing-icon"], [1, "trailing-icon", 3, "click"], [3, "classes", "height", "width"]],
        template: function NgxChipComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵtemplate(0, NgxChipComponent_Conditional_0_Template, 7, 7, "div", 0)(1, NgxChipComponent_Conditional_1_Template, 1, 3);
          }
          if (rf & 2) {
            i0.ɵɵconditional(0, !ctx.isLoading() ? 0 : 1);
          }
        },
        dependencies: [NgxIcon, NgxSkeletonLoadingComponent],
        styles: ["[_nghost-%COMP%]{width:fit-content}.trailing-icon[_ngcontent-%COMP%]{overflow:hidden}.chip[_ngcontent-%COMP%], .chip-label[_ngcontent-%COMP%]{white-space:nowrap}.h-24[_ngcontent-%COMP%]{height:24px}.h-28[_ngcontent-%COMP%]{height:28px}.h-40[_ngcontent-%COMP%]{height:40px}"],
        data: {
          animation: [trigger('appear', [state('void', style({
            width: 0
          })), state('*', style({
            width: '*'
          })), transition('void <=> *', [animate('0.35s ' + 'cubic-bezier(0.15, 0.65, 0.18, 1.09)')])])]
        },
        changeDetection: 0
      });
    }
  }
  return NgxChipComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-chip
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxChipComponent };
